import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight, Heart, CheckCircle } from 'lucide-react';

interface Testimonial {
  id: string;
  name: string;
  age: number;
  location: string;
  avatar: string;
  rating: number;
  text: string;
  highlight: string;
  verified: boolean;
}

const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Sarah M.',
    age: 28,
    location: 'New York, USA',
    avatar: '👩🏻‍💼',
    rating: 5,
    text: "THREEDOT has completely transformed how I understand my body. The predictions are incredibly accurate, and I love how it explains everything in simple terms. It's like having a personal health coach!",
    highlight: "Incredibly accurate predictions",
    verified: true
  },
  {
    id: '2',
    name: 'Priya K.',
    age: 32,
    location: 'Mumbai, India',
    avatar: '👩🏽‍⚕️',
    rating: 5,
    text: "As a working mom, I needed something reliable and easy to use. The multilingual support is amazing, and my teenage daughter is learning to track her cycle too. It's brought us closer together.",
    highlight: "Perfect for busy moms",
    verified: true
  },
  {
    id: '3',
    name: 'Emma L.',
    age: 24,
    location: 'London, UK',
    avatar: '👩🏼‍🎓',
    rating: 5,
    text: "I was skeptical about period tracking apps, but THREEDOT is different. The AI insights helped me identify patterns I never noticed. My doctor was impressed with the data I could share!",
    highlight: "Doctor-approved insights",
    verified: true
  },
  {
    id: '4',
    name: 'Maria S.',
    age: 35,
    location: 'Barcelona, Spain',
    avatar: '👩🏻‍🏫',
    rating: 5,
    text: "After years of irregular cycles, THREEDOT helped me understand what was normal for MY body. The personalized recommendations have made such a difference in my daily life.",
    highlight: "Personalized for my body",
    verified: true
  },
  {
    id: '5',
    name: 'Aisha N.',
    age: 26,
    location: 'Lagos, Nigeria',
    avatar: '👩🏿‍💻',
    rating: 5,
    text: "The privacy features give me peace of mind, and the beautiful design makes tracking actually enjoyable. I've recommended it to all my friends - we even compare our wellness journeys!",
    highlight: "Beautiful and private",
    verified: true
  }
];

const TestimonialCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="relative bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 rounded-3xl p-8 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-pink-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-purple-200/20 to-indigo-200/20 rounded-full blur-2xl"></div>

      <div className="relative z-10">
        <div className="text-center mb-8">
          <motion.div
            className="inline-flex items-center space-x-2 bg-white/60 backdrop-blur-sm rounded-full px-4 py-2 mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <Heart className="w-4 h-4 text-pink-500" />
            <span className="text-sm font-medium text-gray-700">Loved by Women Worldwide</span>
          </motion.div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Real Stories, Real Results</h2>
          <p className="text-gray-600">Join thousands of women who've transformed their health journey</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentTestimonial.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/30"
            >
              <div className="flex items-start space-x-6">
                <motion.div
                  className="flex-shrink-0"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-2xl shadow-lg">
                    {currentTestimonial.avatar}
                  </div>
                </motion.div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{currentTestimonial.name}</h3>
                        {currentTestimonial.verified && (
                          <motion.div
                            className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3 }}
                          >
                            <CheckCircle className="w-3 h-3" />
                            <span>Verified</span>
                          </motion.div>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        Age {currentTestimonial.age} • {currentTestimonial.location}
                      </p>
                    </div>

                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ delay: 0.1 * i, type: "spring", stiffness: 200 }}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              i < currentTestimonial.rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div className="relative">
                    <Quote className="absolute -top-2 -left-2 w-8 h-8 text-purple-200" />
                    <blockquote className="text-gray-700 leading-relaxed pl-6 mb-4">
                      "{currentTestimonial.text}"
                    </blockquote>
                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-3 border-l-4 border-purple-400">
                      <p className="text-sm font-medium text-purple-800">
                        💜 "{currentTestimonial.highlight}"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-center space-x-4 mt-8">
            <motion.button
              onClick={prevTestimonial}
              className="p-3 bg-white/60 backdrop-blur-sm rounded-full hover:bg-white/80 transition-all duration-300 shadow-lg"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </motion.button>

            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    setIsAutoPlaying(false);
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-purple-500 w-8'
                      : 'bg-purple-200 hover:bg-purple-300'
                  }`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.8 }}
                />
              ))}
            </div>

            <motion.button
              onClick={nextTestimonial}
              className="p-3 bg-white/60 backdrop-blur-sm rounded-full hover:bg-white/80 transition-all duration-300 shadow-lg"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCarousel;